import { PushToggle } from './PushToggle';
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { Bell, CheckCheck, RotateCcw } from 'lucide-react';
import { useMe } from '../../../shell/me';
import { useMaybeEditor } from '../../../editor/EditorContext';
import { toastStore } from '../../../state/toasts';
import { errorMessageKey } from '../../../pages/ErrorBox';
import { sharingApi } from '../api';
import type { NotificationView } from '../model/notifications';
import {
  badgeText,
  canQuickExtend,
  groupByDay,
  notificationTarget,
  targetPath,
  unreadCount,
} from '../model/notifications';
import { plainText } from '../model/mentions';
import { notifications } from './service';
import '../sharing.css';

/** Asks the open editor (SharingHost) to show a comment without a navigation. */
export const OPEN_COMMENT_EVENT = 'stageplottter:open-comment';
/** Same for open items (handled by the open items feature). */
export const OPEN_OPEN_ITEM_EVENT = 'stageplottter:open-open-item';

function Text({ n }: { n: NotificationView }) {
  const { t } = useTranslation();
  const p = {
    name: n.authorName || n.actorName || n.guestName || '?',
    board: n.boardName,
    link: n.linkName,
    title: n.title,
  };
  switch (n.type) {
    case 'comment.reply':
      return <>{t('sharing.notifications.reply', p)}</>;
    case 'comment.mention':
      return <>{t('sharing.notifications.mention', p)}</>;
    case 'comment.new':
      return <>{t('sharing.notifications.new', p)}</>;
    case 'link.extension_requested':
      return (
        <>
          {n.guestName
            ? t('sharing.notifications.extensionBy', { ...p, name: n.guestName })
            : t('sharing.notifications.extension', p)}
        </>
      );
    case 'link.expiring':
      return <>{t('sharing.notifications.expiring', p)}</>;
    case 'openitem.assigned':
      return <>{t('sharing.notifications.openitemAssigned', p)}</>;
    case 'openitem.status':
      return (
        <>
          {t('sharing.notifications.openitemStatus', {
            ...p,
            status: n.status ? t(`openitems.status.${n.status}`) : '?',
          })}
        </>
      );
    case 'openitem.reply':
      return <>{t('sharing.notifications.openitemReply', p)}</>;
  }
}

/** App-header bell (slot `notifications`): unread count, grouped list, quick actions. */
export function NotificationsBell() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const editor = useMaybeEditor();
  const navigate = useNavigate();
  const guest = editor?.guest ?? null;
  const enabled = me.status === 'ready' || guest !== null;
  const items = useStore(notifications.store, (s) => s.items);
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const btn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => (enabled ? notifications.acquire() : undefined), [enabled]);
  useEffect(() => {
    if (!enabled) return;
    const f = () => notifications.poke();
    window.addEventListener('focus', f);
    return () => window.removeEventListener('focus', f);
  }, [enabled]);
  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>('button')?.focus();
    const onDoc = (e: PointerEvent) => {
      if (!panel.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      btn.current?.focus();
    };
    document.addEventListener('pointerdown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = unreadCount(items);
  const badge = badgeText(unread);

  const go = (n: NotificationView) => {
    void notifications.markRead([n.id]);
    setOpen(false);
    const target = notificationTarget(n);
    if (!target) return;
    if (editor && target.boardId === editor.boardId && target.kind === 'comment') {
      window.dispatchEvent(
        new CustomEvent(OPEN_COMMENT_EVENT, { detail: { commentId: target.commentId } }),
      );
      return;
    }
    if (editor && target.boardId === editor.boardId && target.kind === 'openitem') {
      window.dispatchEvent(
        new CustomEvent(OPEN_OPEN_ITEM_EVENT, { detail: { openItemId: target.openItemId } }),
      );
      return;
    }
    if (!guest) void navigate(targetPath(target));
  };

  const extend = (n: NotificationView) => {
    sharingApi()
      .updateLink(n.linkId!, { action: 'extend' })
      .then(
        (l) => {
          void notifications.markRead([n.id]);
          toastStore.getState().push({
            kind: 'success',
            message: t('sharing.notifications.extended', {
              name: l.name,
              date: new Date(l.expiresAt).toLocaleDateString(i18n.language),
            }),
          });
        },
        (err: unknown) =>
          toastStore.getState().push({ kind: 'error', message: t(errorMessageKey(err)) }),
      );
  };

  return (
    <div className="menu notifications">
      <button
        ref={btn}
        type="button"
        className="icon-btn bell"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={id}
        aria-label={
          unread > 0
            ? t('sharing.notifications.labelUnread', { count: unread })
            : t('shell.notifications')
        }
        disabled={!enabled}
        onClick={() => {
          setNow(new Date());
          setOpen((o) => !o);
        }}
      >
        <Bell size={18} />
        {badge && (
          <span className="bell-badge" aria-hidden="true">
            {badge}
          </span>
        )}
      </button>
      {open && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Escape closes the popover
        <div
          ref={panel}
          id={id}
          role="dialog"
          aria-label={t('shell.notifications')}
          className="menu-list menu-end notifications-panel"
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape') {
              e.preventDefault();
              setOpen(false);
              btn.current?.focus();
            }
          }}
        >
          <div className="notifications-head">
            <strong>{t('shell.notifications')}</strong>
            <button
              type="button"
              className="btn btn-small btn-ghost"
              disabled={unread === 0}
              onClick={() => void notifications.markRead('all')}
            >
              <CheckCheck size={14} aria-hidden="true" /> {t('sharing.notifications.markAll')}
            </button>
          </div>
          {!guest && <PushToggle />}
          {items.length === 0 && <p className="muted small">{t('sharing.notifications.empty')}</p>}
          {groupByDay(items, now).map((g) => (
            <section key={g.key} aria-label={t(`sharing.notifications.group.${g.key}`)}>
              <h3 className="panel-subtitle">{t(`sharing.notifications.group.${g.key}`)}</h3>
              <ul className="notification-list">
                {g.items.map((n) => (
                  <li key={n.id} className={`notification${n.read ? '' : ' is-unread'}`}>
                    <button type="button" className="notification-main" onClick={() => go(n)}>
                      <span>
                        <Text n={n} />
                      </span>
                      {(n.excerpt || n.message) && (
                        <span className="muted small notification-excerpt">
                          {plainText(n.excerpt || n.message)}
                        </span>
                      )}
                      <time className="muted small" dateTime={n.createdAt}>
                        {new Date(n.createdAt).toLocaleString(i18n.language, {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </time>
                    </button>
                    {!guest && canQuickExtend(n) && (
                      <button type="button" className="btn btn-small" onClick={() => extend(n)}>
                        <RotateCcw size={13} aria-hidden="true" />{' '}
                        {t('sharing.notifications.extend')}
                      </button>
                    )}
                    {!n.read && (
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label={t('sharing.notifications.markRead')}
                        onClick={() => void notifications.markRead([n.id])}
                      >
                        <CheckCheck size={13} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
